import clsx from "clsx";
import { _ } from "i18n";
import { Link } from "react-router";
import { RatingStar } from "ui-kit-v2/src/svg-components/rating-star";
import { formatPlayerRatingScore } from "../../../format-player-rating-score";
import { getPlayerRatingStarColor } from "../../../get-player-rating-star-color";
import { useVideoPlayer } from "../../../Contexts/contexts";
import { handlePlayEvent } from "../../../functions";
import { useGameStore } from "../../../store";
import {
	EventFilterCondition,
	EventFilterValue,
	GPGamePlayerResponse,
	GPGameTeamResponse,
	GPPermissionsResponse,
	PlayerGradingSubmitPayload,
	PlayerRatingModalData,
	VideoSidebar,
} from "../../../types";
import { PlayerStatType } from "../../PlayerStats/definition";
import { createStatTypeConditions } from "../Filter/conditionFactory";
import { createStatTypeFilterValues } from "../Filter/filterFactory";
import { PlayerEventsFilterModal } from "./PlayerEventsFilterModal";
import { PlayerGradingModal } from "./PlayerGradingModal";
import "./PlayerTeamSheet.css";

interface TeamSheetPlayersTableProps {
	setPlayerRatingModal: React.Dispatch<React.SetStateAction<PlayerRatingModalData>>;
	onSubmitPlayerGrading?: (payload: PlayerGradingSubmitPayload) => void | Promise<void>;
	toastMessage: (type: string, message: string) => void;
	players: GPGamePlayerResponse[];
	permissions: GPPermissionsResponse;
	tableName?: string;
	gameTeam: GPGameTeamResponse | null;
}

export function TeamSheetPlayersTable({
	players,
	permissions,
	tableName,
	gameTeam,
	setPlayerRatingModal,
	onSubmitPlayerGrading,
	toastMessage,
}: TeamSheetPlayersTableProps) {
	const { setFilterValues, setFilteredFilterValues, eventFilter, setSidebar } = useGameStore();
	const { playerRef } = useVideoPlayer();

	const showActionsColumn = permissions.showEvents || Boolean(permissions.playerGrading && onSubmitPlayerGrading);

	const onGoalClick = (playerId: number | null) => {
		if (playerId !== null) {
			const filterValues = createStatTypeFilterValues(PlayerStatType.Goal);
			eventFilter.mutateFilterValuesByPlayerId(filterValues, playerId);

			setFilterValues(filterValues);
			setFilteredFilterValues(filterValues);
			handlePlayEvent(playerRef);
			setSidebar(VideoSidebar.EventList);
		}
	};

	const calculateEventGoals = (playerId: number | null): number => {
		if (playerId !== null) {
			const conditions = createStatTypeConditions(PlayerStatType.Goal);
			conditions.push({
				type: "player",
				value: [playerId.toString()],
			} as EventFilterCondition);

			return eventFilter?.filterByConditions(conditions, eventFilter.events).length;
		}

		return 0;
	};

	return (
		<>
			<table className="table">
				<thead className="team-sheet-table-header">
					<tr className="team-sheet-table-title">
						<th colSpan={4 + (permissions.showRatings ? 1 : 0) + (showActionsColumn ? 1 : 0)}>
							{tableName && <div className="player-header__tableName">{tableName}</div>}
						</th>
					</tr>

					<tr>
						<th scope="col">{_("match.team.num")}</th>
						<th scope="col" className="playername">
							{_("matchSheet.table.playerName")}
						</th>
						<th scope="col" className="table-icon">
							<span className="icon-new icon-20 icon icon-black icon-soccer-ball" />
						</th>
						<th scope="col">{_("match.team.yob")}</th>
						{permissions.showRatings && (
							<th className="matchTeam__table__header-rating" scope="col">
								{_("game.stats.table.rating")}
							</th>
						)}
						{showActionsColumn && (
							<th className="matchTeam__table__playerHighlights" scope="col">
								{_("matchSheet.table.actions")}
							</th>
						)}
					</tr>
				</thead>

				<tbody>
					{players.map((player) => {
						const isAllowed = permissions.allowedPlayers === null || permissions.allowedPlayers.includes(player.id || 0);
						const filterValues: EventFilterValue[] = eventFilter?.mutateFilterValuesByPlayerId([], player.id as number);
						const events = isAllowed ? eventFilter?.filter(filterValues) : [];
						const hasGoal = player.goals !== null && player.goals > 0;
						const isGoalClickable = isAllowed && hasGoal && permissions.showEvents && calculateEventGoals(player.id) !== 0;

						return (
							<tr key={`team-sheet-${player.id}`}>
								<td>{player.numberSign}</td>
								<td className="team-sheet_player_name">
									{player.coachRating && (
										<div
											className={clsx("team-sheet_player_rating", getPlayerRatingStarColor(player.coachRating?.score))}
											onClick={() => {
												if (gameTeam?.permissions.ratingBadgeEdit) {
													setPlayerRatingModal({
														show: true,
														id: player.id ?? 0,
														name: player.name,
														rating: player.coachRating,
													});
												}
											}}
										>
											<RatingStar size={18} />
											<div>{formatPlayerRatingScore(player.coachRating?.score)}</div>
										</div>
									)}
									<div className="player-tooltip-wrapper">
										{player.playerUrl ? (
											<Link target="_blank" to={player.playerUrl}>
												{player.name}
											</Link>
										) : (
											player.name
										)}
										{player.position && player.position !== null && player.position.trim() !== "" && (
											<div className="player-tooltip">
												<div>{player.positionKey}</div>
											</div>
										)}
									</div>
								</td>
								<td
									className={`${isGoalClickable ? "team-sheet__goal--clickable" : ""}`}
									onClick={isGoalClickable ? () => onGoalClick(player.id) : undefined}
								>
									{hasGoal ? player.goals : ""}
								</td>
								<td>{player.birthYear}</td>
								{permissions.showRatings && <td>{player.rating}</td>}
								{showActionsColumn && (
									<td>
										{player.id ? (
											<div className="team-sheet-player-actions">
												{permissions.playerGrading && onSubmitPlayerGrading ? (
													<PlayerGradingModal
														gradeId={player.playerGrading?.id ?? null}
														playerId={player.id}
														playerName={player.name}
														gameTeam={gameTeam}
														playerGrading={player.playerGrading ?? null}
														gradedByScouts={player.gradedByScouts ?? null}
														onSubmit={onSubmitPlayerGrading}
														toastMessage={toastMessage}
													/>
												) : null}
												{permissions.showEvents && (
													<PlayerEventsFilterModal
														disabled={events?.length === 0}
														clipsAllowed={permissions.useClips}
														playerId={player.id}
													/>
												)}
											</div>
										) : null}
									</td>
								)}
							</tr>
						);
					})}
				</tbody>
			</table>
		</>
	);
}
